import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import { SkeletonTheme } from 'react-loading-skeleton';
import LandingPage from './components/pages/LandingPage';
import EnterpriseLandingPage from './components/pages/EnterpriseLandingPage';
import Login from './components/pages/Login';
import PlatformAdminDashboard from './components/platform/PlatformAdminDashboard';
import AdminDashboard from './components/dashboards/AdminDashboard';
import ClientDashboard from './components/dashboards/ClientDashboard';
import VendorDashboard from './components/dashboards/VendorDashboard';
import ReferralPage from './components/pages/ReferralPage';
import LegalPage from './components/pages/LegalPage';
import ForgotPassword from './components/pages/ForgotPassword';
import ResetPassword from './components/pages/ResetPassword';
import { FullPageSkeleton } from './components/ui/PageSkeleton';
import ScrollToTop from './components/ui/ScrollToTop';
import DomainRouteGuard from './components/DomainRouteGuard';
import VersionBadge from './components/ui/VersionBadge';
import { SKELETON_THEME } from './styles/skeletonTheme';

function ProtectedRoute({ children, adminOnly = false, vendorOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullPageSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (vendorOnly && !user.is_vendor) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PublicRoute({ children, allowAuthenticated = false }) {
  const { user } = useAuth();

  if (allowAuthenticated) {
    return children;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute allowAuthenticated={true}>
            <LandingPage />
          </PublicRoute>
        }
      />

      <Route
        path="/enterprise"
        element={
          <PublicRoute allowAuthenticated={true}>
            <EnterpriseLandingPage />
          </PublicRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route path="/referral/:linkCode" element={<ReferralPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />

      <Route
        path="/admin/*"
        element={
          <ProtectedRoute adminOnly={true}>
            {user?.is_admin && !user?.company_id ? <PlatformAdminDashboard /> : <AdminDashboard />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/*"
        element={
          <ProtectedRoute vendorOnly={true}>
            <VendorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          user?.is_admin ? <Navigate to="/admin" replace /> :
          user?.is_vendor ? <Navigate to="/vendor" replace /> :
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

const toastStyle = {
  background: 'rgba(255, 255, 255, 0.97)',
  color: '#334155',
  boxShadow: '0 16px 48px -12px rgba(249, 115, 22, 0.18), 0 8px 24px -8px rgba(15, 23, 42, 0.12)',
  border: '1px solid rgba(251, 146, 60, 0.25)',
  borderRadius: '16px',
  backdropFilter: 'blur(16px)',
  padding: '14px 18px',
  fontSize: '14px',
  fontWeight: '500',
  maxWidth: '420px',
};

function AppWithTheme() {
  const { t } = useLanguage();

  return (
    <SkeletonTheme
      baseColor={SKELETON_THEME.baseColor}
      highlightColor={SKELETON_THEME.highlightColor}
      borderRadius={SKELETON_THEME.borderRadius}
      duration={SKELETON_THEME.duration}
    >
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <MotionConfig reducedMotion="user">
          <a href="#main-content" className="skip-link">
            {t.common?.skipToContent || 'Skip to content'}
          </a>
          <ScrollToTop />
          <div className="min-h-screen">
            <DomainRouteGuard>
              <AppRoutes />
            </DomainRouteGuard>
            <VersionBadge />
            <Toaster
              position="top-center"
              gutter={12}
              containerStyle={{ top: 20 }}
              toastOptions={{
                duration: 4000,
                style: toastStyle,
                success: {
                  duration: 3500,
                  iconTheme: {
                    primary: '#f97316',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </MotionConfig>
      </Router>
    </SkeletonTheme>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <AppWithTheme />
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </LanguageProvider>
  );
}

export default App;
